import React, { useContext, useEffect, useRef, useState } from "react";
import ChevronRight from "@assets/svg/chevron-right.svg?react";
import ChevronLeft from "@assets/svg/chevron-left.svg?react";
import {
  SectionWrapper,
  OtpBox,
  Title,
  EmailInfo,
  OtpInputs,
  OtpInput,
  ResendWrapper,
  ConfirmWrapper,
  ConfirmButton,
} from "./styles";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import useBreakPoinst from "@hooks/useBreakPoinst";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { useSubscription } from "@services";
import MsoolLoader from "../formSection/MsoolLoader";
import { ToastMessageContext } from "@context/toast-message-cotnext";

type OtpForm = {
  otp0: string;
  otp1: string;
  otp2: string;
  otp3: string;
};

const OtpSection = () => {
  const [enabledIndex, setEnabledIndex] = useState(0);
  const { showError, showSuccess } = useContext(ToastMessageContext);
  const [countdown, setCountdown] = useState<number>(0);
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const { isPhone } = useBreakPoinst();
  const { verifyOTPMutate, resendOtpMutate } = useSubscription();
  const { mutate, isPending } = verifyOTPMutate();
  const { mail } = useParams();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
  } = useForm<OtpForm>({
    mode: "onChange",
  });

  const otpRefs = useRef<HTMLInputElement[]>([]);

  const onSubmit: SubmitHandler<OtpForm> = (data) => {
    const otpCode = `${data.otp0}${data.otp1}${data.otp2}${data.otp3}`;

    mutate(
      {
        email: decodeURIComponent(mail as string),
        otp: otpCode,
      },
      {
        onSuccess: (res) => {
          showSuccess(t("Signup.Account_created_successfully"));
          navigate("/login", {
            state: { email: decodeURIComponent(mail as string) },
          });
        },
        onError: (error) => {},
      }
    );

    setCountdown(30);
  };

  const { mutate: resendOtp, isPending: isResending } = resendOtpMutate();

  const handleResend = () => {
    resendOtp({
      email: decodeURIComponent(mail as string),
    });
    if (countdown > 0) return;
    // TODO: Add actual resend API call here
    setCountdown(30);
  };
  useEffect(() => {
    otpRefs.current[0]?.focus();
  }, []);

  const handleOtpChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    onChange: (...event: any[]) => void
  ) => {
    const value = e.target.value;

    // Only allow single digits
    if (!/^\d?$/.test(value)) return;

    onChange(value); // update form value

    // If input has value and it's the current allowed index
    if (value && index === enabledIndex) {
      if (index < 3) {
        setEnabledIndex(index + 1); // Enable next input
        setTimeout(() => {
          otpRefs.current[index + 1]?.focus(); // Focus next
        }, 10);
      } else {
        // Last digit typed
        setTimeout(() => {
          handleSubmit(onSubmit)();
        }, 50);
      }
    }
  };

  useEffect(() => {
    if (countdown === 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  const formatCountdown = (seconds: number) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  useEffect(() => {
    setEnabledIndex(0);
    otpRefs.current[0]?.focus();
  }, [countdown]);

  if (isPending || isResending) {
    return <MsoolLoader Text={t("Signup.sending")} />;
  }

  return (
    <SectionWrapper $isPhone={isPhone}>
      <OtpBox $isPhone={isPhone}>
        {language === "ar" ? (
          <ChevronRight
            onClick={() => navigate("/register")}
            style={{ cursor: "pointer" }}
          />
        ) : (
          <ChevronLeft
            onClick={() => navigate("/register")}
            style={{ cursor: "pointer" }}
          />
        )}

        <Title $isPhone={isPhone}>{t("Signup.Enter_Ver_code")}</Title>

        <EmailInfo $isPhone={isPhone}>
          <span>{t("Signup.emailSentMessage") + maskEmail(mail)}</span>
          <span onClick={() => navigate("/register")}>
            {t("Signup.change_email")}
          </span>
        </EmailInfo>

        <form onSubmit={handleSubmit(onSubmit)}>
          <OtpInputs>
            {[0, 1, 2, 3].map((index) => (
              <Controller
                key={index}
                name={`otp${index}` as keyof OtpForm}
                control={control}
                rules={{
                  required: true,
                  pattern: /^[0-9]$/,
                }}
                render={({ field }) => (
                  <OtpInput
                    {...field}
                    ref={(el) => {
                      field.ref(el);
                      if (el) otpRefs.current[index] = el;
                    }}
                    maxLength={1}
                    type="text"
                    $isPhone={isPhone}
                    $hasError={!!errors[`otp${index}` as keyof OtpForm]}
                    disabled={index > enabledIndex}
                    onChange={(e) => handleOtpChange(e, index, field.onChange)}
                  />
                )}
              />
            ))}
          </OtpInputs>

          {Object.keys(errors).length > 0 && (
            <p style={{ color: "red", textAlign: "center", marginTop: 8 }}>
              {t("Signup.otp_invalid")}
            </p>
          )}

          <ResendWrapper>
            {countdown > 0 && <span>{formatCountdown(countdown)}</span>}
            <span
              onClick={handleResend}
              style={{
                pointerEvents: countdown > 0 ? "none" : "auto",
                opacity: countdown > 0 ? 0.4 : 1,
              }}
            >
              {t("Signup.resend")}
            </span>
          </ResendWrapper>

          <ConfirmWrapper>
            <ConfirmButton
              $isPhone={isPhone}
              disabled={!isValid || isPending}
              type="submit"
            >
              {isPending ? t("Signup.loading") : t("Signup.confirm")}
            </ConfirmButton>
          </ConfirmWrapper>
        </form>
      </OtpBox>
    </SectionWrapper>
  );
};

export default OtpSection;
function maskEmail(email = "") {
  const [localPart, domain] = email.split("@");
  if (localPart.length <= 2) {
    return "*".repeat(localPart.length) + "@" + domain;
  }
  const visible = localPart.slice(0, 2);
  const masked = "*".repeat(localPart.length - 2);
  return `${visible}${masked}@${domain}`;
}
